import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, X, Camera, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MaintenanceAPI, UploadsAPI, resolveImageUrl } from "@/lib/api";
import { fromKm, toKm, unitLabel } from "@/lib/units";
import logger from "@/lib/logger";

/**
 * Modal to log a service completion for a maintenance item.
 * Captures date + odometer + cost + notes + optional receipt image.
 * On success calls onCompleted(updatedItem) so parent can refresh.
 */
export default function MaintenanceCompleteDialog({
  open,
  onOpenChange,
  item,
  unit = "km",
  currencySymbol = "$",
  onCompleted,
}) {
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  useEffect(() => {
    if (open && item) {
      const currentOdo = item.current_odometer_km || 0;
      setForm({
        date: new Date().toISOString().slice(0, 10),
        odometer_display: currentOdo > 0 ? String(Math.round(fromKm(currentOdo, unit))) : "",
        cost: "",
        notes: "",
        receipt_url: "",
      });
    }
  }, [open, item, unit]);

  const handleReceipt = useCallback(async (file) => {
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const res = await UploadsAPI.uploadVehiclePhoto(file, file.name || "receipt.jpg");
      setForm((f) => ({ ...f, receipt_url: res.url }));
      toast.success("Receipt attached");
    } catch (err) {
      logger.warn("maintenance-receipt-upload-failed", err?.message || String(err));
      toast.error("Receipt upload failed");
    } finally {
      setUploadingReceipt(false);
    }
  }, []);

  const submit = async () => {
    if (!item) return;
    setSubmitting(true);
    try {
      const odoKm = form.odometer_display !== ""
        ? toKm(parseFloat(form.odometer_display) || 0, unit)
        : null;
      const updated = await MaintenanceAPI.complete(item.id, {
        date: form.date || undefined,
        odometer_km: odoKm,
        cost: form.cost !== "" ? parseFloat(form.cost) : null,
        notes: form.notes || "",
        receipt_url: form.receipt_url || "",
      });
      toast.success("Service logged");
      onCompleted?.(updated);
      onOpenChange(false);
    } catch (err) {
      logger.error("maintenance-complete-failed", err?.message || String(err));
      toast.error("Failed to log service");
    } finally {
      setSubmitting(false);
    }
  };

  if (!item || !form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[480px]"
        data-testid="maintenance-complete-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#34D17A]" strokeWidth={1.75} />
            Log service completion
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--text-muted)]">
            {item.name}. This resets the countdown for the next {item.interval_km ? "distance" : "time"} interval.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="space-y-1">
            <Label className="label-tiny">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-complete-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Odometer ({unitLabel(unit)})</Label>
            <Input
              type="number"
              value={form.odometer_display}
              onChange={(e) => setForm({ ...form, odometer_display: e.target.value })}
              placeholder="—"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-complete-odometer"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Cost ({currencySymbol})</Label>
            <Input
              type="number"
              step="0.01"
              value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
              placeholder="0.00"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-complete-cost"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Receipt (optional)</Label>
            <div className="flex items-center gap-2">
              <label
                className="tap flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
                style={{ borderRadius: 2 }}
                data-testid="maintenance-complete-receipt-btn"
              >
                {uploadingReceipt ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Camera className="w-3.5 h-3.5" strokeWidth={1.75} />
                )}
                {form.receipt_url ? "Replace" : "Attach"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleReceipt(e.target.files?.[0])}
                />
              </label>
              {form.receipt_url && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, receipt_url: "" })}
                  className="tap h-9 w-9 flex items-center justify-center bg-[var(--bg-canvas)] border border-[var(--border-hover)] text-[var(--text-muted)] hover:text-[#FF3B30]"
                  style={{ borderRadius: 2 }}
                  aria-label="Remove receipt"
                  data-testid="maintenance-complete-receipt-remove"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              )}
            </div>
            {form.receipt_url && (
              <img
                src={resolveImageUrl(form.receipt_url)}
                alt="Receipt"
                className="mt-2 w-full h-16 object-cover border border-[var(--border-hover)]"
                style={{ borderRadius: 2 }}
              />
            )}
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="label-tiny">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Shop, technician, oil brand, etc."
              rows={2}
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
              data-testid="maintenance-complete-notes"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            style={{ borderRadius: 2 }}
            data-testid="maintenance-complete-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="flex-1 bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
            style={{ borderRadius: 2 }}
            data-testid="maintenance-complete-submit"
          >
            {submitting ? "Saving..." : "Log service"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
